/**
 * 🔴 O FILTRO DE GRUPOS — um só, para as duas telas que listam grupos.
 *
 * ⚠️ Ele nasceu dentro do `SeletorDeGrupos` dos Comunicados, na Parte 1 do
 * disparo por grupo (11/09). A aba **Grupos** do WhatsApp precisava do mesmo —
 * e o handoff de 12/09 foi explícito: ***"Não crie um segundo mecanismo de
 * filtro — reuse."***
 *
 * 🔴 Por isso ele foi EXTRAÍDO, não copiado. As duas telas passam pelo mesmo
 * predicado e pelos mesmos rótulos: se um dia "só onde o bot está" mudar de
 * significado, muda nos dois lugares de uma vez. Duas definições do mesmo
 * filtro divergindo em silêncio é a família de defeito que este projeto já
 * pagou três vezes.
 *
 * ⚠️ **O SELO do bot também mora aqui** (o componente está no `SeloDoBot.tsx`
 * ao lado — arquivo que exporta SÓ o componente, porque o `react-refresh`
 * reclama de arquivo que mistura componente com constante). As palavras que descrevem o estado
 * ("bot dentro", "bot fora", "bot nunca conferido") são as mesmas nas duas
 * telas de propósito: o tri-estado só ensina se for dito igual em todo lugar.
 */
import { useMemo, useState, type ReactNode } from 'react';

import { Input, Select } from '@/components/ui';

/**
 * 🔴 O TRI-ESTADO (09/09). `fora = false` **sem conferência não é "dentro"**:
 * o número saiu dos grupos dos clientes, e dizer verde sem ter perguntado à
 * Evolution seria mentir.
 */
export type EstadoDoBot = 'dentro' | 'fora' | 'nunca-conferido';

/** O mínimo que uma tela precisa expor para ser filtrada por aqui. */
export interface GrupoFiltravel {
  grupoId: string;
  /** O nome que o Matheus vê no celular — o da Evolution, quando há. */
  nome: string;
  /** Nomes dos clientes/tipos que o grupo acompanha — entram na busca. */
  acompanha: string[];
  clienteIds: string[];
  porTipo: boolean;
  interno: boolean;
  bot: EstadoDoBot;
}

export const SELO_DO_BOT: Record<
  EstadoDoBot,
  { texto: string; variant: 'success' | 'danger' | 'warning' }
> = {
  dentro: { texto: 'bot dentro', variant: 'success' },
  fora: { texto: 'bot fora', variant: 'danger' },
  'nunca-conferido': { texto: 'bot nunca conferido', variant: 'warning' },
};

export interface FiltroDeGrupos {
  busca: string;
  bot: 'todos' | 'dentro' | 'sem';
  tipo: 'todos' | 'cliente' | 'interno' | 'tipo';
  cliente: string;
}

const VAZIO: FiltroDeGrupos = { busca: '', bot: 'todos', tipo: 'todos', cliente: '' };

/**
 * O predicado — **exportado de propósito**, para o teste poder cobrá-lo sem
 * montar tela nenhuma, e para quem precisar filtrar fora de um componente.
 */
export function passaNoFiltro(g: GrupoFiltravel, f: FiltroDeGrupos): boolean {
  const b = f.busca.trim().toLowerCase();
  if (b && !`${g.nome} ${g.acompanha.join(' ')}`.toLowerCase().includes(b)) return false;
  if (f.bot === 'dentro' && g.bot !== 'dentro') return false;
  if (f.bot === 'sem' && g.bot === 'dentro') return false;
  if (f.tipo === 'interno' && !g.interno) return false;
  if (f.tipo === 'tipo' && !g.porTipo) return false;
  if (f.tipo === 'cliente' && (g.interno || g.clienteIds.length === 0)) return false;
  if (f.cliente && !g.clienteIds.includes(f.cliente)) return false;
  return true;
}

/**
 * O filtro inteiro: estado, lista filtrada e os controles prontos para a tela
 * pôr onde quiser.
 *
 * @param idPrefixo prefixo dos `id`/`htmlFor` — duas listas na mesma página
 *        não podem compartilhar id (o rótulo passaria a focar o campo errado).
 */
export function useFiltroDeGrupos<T extends GrupoFiltravel>(
  grupos: T[],
  opts: { idPrefixo: string; testIdPrefixo?: string },
): { filtrados: T[]; filtro: FiltroDeGrupos; controles: ReactNode; limpar: () => void } {
  const [filtro, setFiltro] = useState<FiltroDeGrupos>(VAZIO);
  const t = opts.testIdPrefixo ?? opts.idPrefixo;

  /** Os clientes que aparecem NOS GRUPOS — nunca a lista inteira do sistema. */
  const clientes = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of grupos) {
      g.clienteIds.forEach((id, i) => {
        if (!m.has(id)) m.set(id, g.acompanha[i] ?? id);
      });
    }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'));
  }, [grupos]);

  const filtrados = useMemo(
    () => grupos.filter((g) => passaNoFiltro(g, filtro)),
    [grupos, filtro],
  );

  const controles = (
    <div className="flex flex-wrap items-end gap-2">
      <div className="min-w-40 flex-1">
        <label htmlFor={`${opts.idPrefixo}-busca`} className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
          Procurar pelo nome
        </label>
        <Input
          id={`${opts.idPrefixo}-busca`}
          value={filtro.busca}
          onChange={(e) => setFiltro((f) => ({ ...f, busca: e.target.value }))}
          placeholder="Nome do grupo ou do cliente"
          data-testid={`${t}-busca-grupo`}
        />
      </div>
      <div>
        <label htmlFor={`${opts.idPrefixo}-bot`} className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
          O bot
        </label>
        <Select
          id={`${opts.idPrefixo}-bot`}
          value={filtro.bot}
          onChange={(e) => setFiltro((f) => ({ ...f, bot: e.target.value as FiltroDeGrupos['bot'] }))}
          data-testid={`${t}-filtro-bot`}
        >
          <option value="todos">Todos os grupos</option>
          <option value="dentro">Só onde o bot está</option>
          <option value="sem">Só onde o bot NÃO está</option>
        </Select>
      </div>
      <div>
        <label htmlFor={`${opts.idPrefixo}-tipo`} className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
          Tipo
        </label>
        <Select
          id={`${opts.idPrefixo}-tipo`}
          value={filtro.tipo}
          onChange={(e) => setFiltro((f) => ({ ...f, tipo: e.target.value as FiltroDeGrupos['tipo'] }))}
          data-testid={`${t}-filtro-tipo`}
        >
          <option value="todos">Todos</option>
          <option value="cliente">De cliente</option>
          <option value="tipo">Por tipo de tarefa (Route)</option>
          <option value="interno">Internos da IGM</option>
        </Select>
      </div>
      {clientes.length > 0 && (
        <div className="min-w-40">
          <label htmlFor={`${opts.idPrefixo}-cliente`} className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
            Cliente
          </label>
          <Select
            id={`${opts.idPrefixo}-cliente`}
            value={filtro.cliente}
            onChange={(e) => setFiltro((f) => ({ ...f, cliente: e.target.value }))}
            data-testid={`${t}-filtro-cliente`}
          >
            <option value="">Qualquer um</option>
            {clientes.map(([id, nome]) => (
              <option key={id} value={id}>
                {nome}
              </option>
            ))}
          </Select>
        </div>
      )}
    </div>
  );

  return { filtrados, filtro, controles, limpar: () => setFiltro(VAZIO) };
}
